import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';

import OutputText from '@ey/canvascoreservices/OutputText';

import Utility from '../../../../util/utilityfunctions';
import {currentResource} from '../../../../util/utils';
import {groupRiskAssessmentTabId} from '../../../../util/uiconstants';
import {adobeAnalytics} from '@ey/voltron-analytics';
import selectLastRefreshedFromSpecificTab from '../../../../reducers/GroupRiskAsssesment/selectLastRefreshedFromSpecificTab';

import ActivityFeed from '../ActivityFeed';
import Reconciliations from '../Reconciliations';

import TemplateWithSidebar from '../../../Templates/TemplateWithSidebar';
import TabNavigation from '../../../CoreWrappers/TabNavigation';
import ShowMoreLess from '../../../CoreWrappers/ShowMoreLess';
import RightDashboardLoader from '../../../Common/Loaders/RightDashboardLoader';
import Sidebar from '../../../Common/Sidebar';
import LabelAndDate from '../../../Common/LabelAndDate';
import StyledGroupRiskAssessment from '../StyledGroupRiskAssessment';
import {eventTypes, controlTypes, pageNames, siteSections, clickActionNames, locationInPages} from '../../../AdobeAnalytics/constants';
import {clickActionArgs, pageViewArgs} from '../../../AdobeAnalytics/actionHelpers';

const TableView = () => {
	const [activeTab, setActiveTab] = useState(null);
	const labels = currentResource.groupRiskAssessmentLabels;
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const lastRefreshedObj = selectLastRefreshedFromSpecificTab(activeTab?.id ?? null);

	const history = useHistory();
	const location = useLocation();
	const [initAdobeTrack, setInitAdobeTrack] = useState(true);
	const trackEvent = adobeAnalytics.useAdobeAnalyticsTrack();

	const tabs = [
		{
			id: groupRiskAssessmentTabId.reconciliations,
			label: labels.reconciliations,
			hash: '#reconciliations',
			visible: true,
			View: Reconciliations
		},
		{
			id: groupRiskAssessmentTabId.activityFeed,
			label: labels.activityFeed,
			hash: '#activityFeed',
			visible: true,
			View: ActivityFeed
		}
	];

	useEffect(() => {
		handleHashAtLoad();
	}, []);

	const trackInitLoad = (name) => {
		trackEvent(
			eventTypes.pageView,
			pageViewArgs(
				name,
				siteSections.gra
			)
		);
		setInitAdobeTrack(false);
	};

	const handleSetTab = (newTab) => {
		history.replace(`${location.search}${newTab.hash}`);
		setActiveTab(newTab);

		// Adobe Analytics
		if (activeTab?.id != newTab?.id && !initAdobeTrack) {
			// Adobe Analytics Click Tracking
			trackEvent(eventTypes.clickAction,
				clickActionArgs(
					newTab?.id == groupRiskAssessmentTabId.reconciliations ? clickActionNames.reconciliations : clickActionNames.activityFeed,
					controlTypes.tab,
					locationInPages.reconciliationsActivity,
					`${window.location.origin}${window.location.pathname}${location.search}${newTab.hash}`
				)
			);
			
			// Adobe Analytics Page Tracking
			trackEvent(
				eventTypes.pageView,
				pageViewArgs(
					newTab?.id == groupRiskAssessmentTabId.reconciliations ? pageNames.reconciliations : pageNames.activityFeed,
					siteSections.gra
				)
			);
		}
	};

	const handleHashAtLoad = () => {
		const hash = location.hash;
		if (!!hash) {
			const hashTab = tabs.find(({hash: hashTag}) => hashTag.toLowerCase() === hash.toLowerCase());
			setActiveTab(hashTab ? hashTab : tabs[0]);

			// Adobe Analytics Page Tracking
			trackInitLoad(hashTab?.id == groupRiskAssessmentTabId.reconciliations ? pageNames.reconciliations : pageNames.activityFeed);
		} else {
			handleSetTab(tabs[0]);

			// Adobe Analytics Page Tracking
			trackInitLoad(pageNames.reconciliations);
		}
	};

	const renderLastRefreshed = () => {
		const props = {
			className: 'last-refreshed',
			label: lastRefreshedObj?.lastRefreshedLabel,
			date: lastRefreshedObj?.lastRefreshedDate,
			body2: true,
			light: true,
			color: 'var(--neutrals-700)'
		};
		return <LabelAndDate {...props} />;
	};

	const renderTabs = () => {
		const props = {
			tabs,
			activeTab,
			setTab: handleSetTab,
			size: 'md',
			customSideContent: renderLastRefreshed
		};

		return <TabNavigation {...props} />;
	};

	const renderSidebar = () => <Sidebar moduleLabel={labels.groupRiskAssessment} />;

	const renderLoader = () => <RightDashboardLoader />;

	const renderInstructional = () => {
		if (activeTab === null) return false;
		const instructionalText =  labels.reconciliations === activeTab?.label ? labels.groupRiskAssessmentInstructional : labels.groupRiskAssessmentActivityFeedInstructional;
		const props = {
			text: Utility.createRichTextElement(instructionalText),
			initialEllipsed: false,
			numLines: 3,
			fontSize: 'body1'
		};

		return <ShowMoreLess {...props} />;
	};

	const renderDisclaimerInstructional = () => {
		
		return <OutputText body3>{Utility.createRichTextElement(labels.disclaimerInstructional)}</OutputText>;
	};

	const renderContent = () => (
		<StyledGroupRiskAssessment>
			{renderInstructional()}
			{renderTabs()}
			{activeTab && <activeTab.View />}
			{renderDisclaimerInstructional()}
		</StyledGroupRiskAssessment>
	);

	return (
		<TemplateWithSidebar renderSidebar={renderSidebar}>
			{!orgChart ? renderLoader() : renderContent()}
		</TemplateWithSidebar>
	);
};

export default TableView;
